import { useId, type ReactNode } from 'react';

import { Checkbox } from '@/components/ui/Checkbox';
import { policyPath, type PublicPolicySummary } from '@/features/legal/api/policies';

/** "A, B and C" - the way the sentence reads aloud. */
function joinLinks(items: ReactNode[]): ReactNode[] {
  return items.flatMap((item, index) => {
    if (index === 0) return [item];
    return [index === items.length - 1 ? ' and ' : ', ', item];
  });
}

export interface PolicyConsentFieldProps {
  policies: PublicPolicySummary[];
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string | null;
}

/**
 * One box, ticked on purpose, naming every policy it agrees to.
 *
 * Never pre-ticked: acceptance only counts as explicit if the person did it.
 * Each name links to the published text and opens in a new tab, so reading
 * the terms does not cost them the half-filled form.
 */
export function PolicyConsentField({ policies, checked, onChange, error }: PolicyConsentFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  if (policies.length === 0) return null;

  const links = policies.map((policy) => (
    <a
      key={policy.slug}
      href={policyPath(policy.slug)}
      target="_blank"
      rel="noreferrer"
      className="font-semibold text-brand-600 underline-offset-2 hover:underline"
    >
      {policy.name}
    </a>
  ));

  return (
    <div>
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="mt-0.5"
        />
        <label htmlFor={id} className="text-sm leading-relaxed text-ink-soft">
          I have read and agree to NeonCarts&apos; {joinLinks(links)}.
        </label>
      </div>
      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
